// High-frequency, NON-authoritative game data over the world's Realtime
// channel (`world:<id>`): drawing strokes, a spinning bottle's live angle,
// pointer trails. Fire-and-forget — no history, no ordering guarantees, and
// anyone in the world channel can read and send it. So:
//
//   - Never put secrets here (the drawing word, trivia answers): those stay in
//     game_hands, written by the `games` function.
//   - Never decide anything from it: scores, guesses, whose turn — all go
//     through tableAction(pid, 'move', …) to the server.
//   - Your own messages are not echoed back (the channel has self: false);
//     draw your local strokes yourself.
//
// Usage (inside a game's Table3D / Overlay / VRPanel):
//
//   useGameFx(worldId, table.id, 'stroke', (data, from) => addStroke(data));
//   const send = useMemo(() => fxBatcher<Pt>((pts) => sendGameFx(worldId, table.id, 'stroke', { pts }), 60), [worldId, table.id]);
//   send.push(point);  // batched: one message per 60 ms
//
// Supabase limits messages per second per client; keep to ≤ ~20/s per
// player (batch points, don't send one message per point).

import { useEffect, useRef } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { getCurrentChannel, getCurrentWorldId, getPresenceKey } from '../../services/realtimeManager';

// We never create or subscribe the world channel here: the viewer's
// multiplayer sync owns it (via realtimeManager.onChannelSubscribed, after it
// has registered its own presence/broadcast handlers — subscribing early
// would join without them). We only add one broadcast binding to the channel
// once it exists, and send while it is joined.

const EVENT = 'game:fx';
type Handler = (data: any, from: string | null) => void;

/** tableId → event → handlers */
const handlers = new Map<string, Map<string, Set<Handler>>>();
/** Channels that already carry our one dispatcher binding. */
const bound = new WeakSet<RealtimeChannel>();

/** The world's channel if the viewer has opened it (binding our dispatcher once). */
function worldChannel(worldId: string): RealtimeChannel | null {
  const channel = getCurrentWorldId() === worldId ? getCurrentChannel() : null;
  if (channel && !bound.has(channel)) {
    bound.add(channel);
    // One binding per channel fans out to every listener (bindings can't be
    // removed individually, so listeners come and go in `handlers`).
    channel.on('broadcast', { event: EVENT }, (msg: any) => {
      const p = msg?.payload;
      if (!p || typeof p.t !== 'string' || typeof p.e !== 'string') return;
      handlers.get(p.t)?.get(p.e)?.forEach((h) => { try { h(p.d, p.f ?? null); } catch (err) { console.warn('[game-fx]', err); } });
    });
  }
  return channel;
}

/** Listen for `event` at table `tableId`. Returns an unsubscribe. */
export function onGameFx(worldId: string, tableId: string, event: string, handler: Handler): () => void {
  let byEvent = handlers.get(tableId);
  if (!byEvent) handlers.set(tableId, (byEvent = new Map()));
  let set = byEvent.get(event);
  if (!set) byEvent.set(event, (set = new Set()));
  set.add(handler);
  // Bind now, and again whenever the viewer (re)opens the world channel.
  worldChannel(worldId);
  const watch = window.setInterval(() => worldChannel(worldId), 1000);
  return () => {
    window.clearInterval(watch);
    set!.delete(handler);
    if (!set!.size) byEvent!.delete(event);
    if (!byEvent!.size) handlers.delete(tableId);
  };
}

/**
 * Send `data` (small JSON) to everyone else in the world, for `tableId`.
 * Dropped (returns false) unless the world channel is joined.
 */
export function sendGameFx(worldId: string, tableId: string, event: string, data: unknown): boolean {
  const channel = worldChannel(worldId);
  if (!channel || (channel as any).state !== 'joined') return false;
  void channel.send({ type: 'broadcast', event: EVENT, payload: { t: tableId, e: event, d: data, f: getPresenceKey() } });
  return true;
}

/** React hook form of onGameFx; the latest handler is always used. */
export function useGameFx(worldId: string | null | undefined, tableId: string | null | undefined, event: string, handler: Handler) {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!worldId || !tableId) return;
    return onGameFx(worldId, tableId, event, (d, f) => ref.current(d, f));
  }, [worldId, tableId, event]);
}

/**
 * Collects items and flushes them as one array every `intervalMs` (and when
 * `maxItems` pile up). Call flush() at the end of a stroke.
 */
export function fxBatcher<T>(send: (items: T[]) => void, intervalMs = 60, maxItems = 64) {
  let buf: T[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;
  const flush = () => {
    if (timer) { clearTimeout(timer); timer = null; }
    if (!buf.length) return;
    const out = buf; buf = [];
    send(out);
  };
  return {
    push(item: T) {
      buf.push(item);
      if (buf.length >= maxItems) flush();
      else if (!timer) timer = setTimeout(flush, intervalMs);
    },
    flush,
    cancel() { if (timer) clearTimeout(timer); timer = null; buf = []; },
  };
}
